import React from 'react';

// Components
import {
  ScrollView,
  Text,
  VStack,
  HStack,
  Spacer,
  View,
  Skeleton,
} from 'native-base';
import { ChatBubble, ErrorAndRefetch } from '../../';

// Assets
import { theme } from '@breakroom-nx/assets';

// Classes & Types
import { Room, User } from '@breakroom-nx/classes';

// Redux
import { useGetChatsForUserQuery } from '@breakroom-nx/redux';
import { ChatType } from '@breakroom-nx/helpers';

export interface ChatListCollectionViewProps {
  user: User;
  isClient: boolean;
}

export function ChatListCollectionView({
  user,
  isClient,
}: ChatListCollectionViewProps) {
  // Redux
  const {
    data: chats,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetChatsForUserQuery({
    uid: user.uid,
    chatType: ChatType.Public,
  });

  const renderChats = () => {
    if (chats && chats.length > 0) {
      return (
        <ScrollView
          showsHorizontalScrollIndicator={true}
          horizontal={true}
          _contentContainerStyle={{
            px: 8,
            py: 5,
          }}
        >
          <HStack space={3}>
            {chats.map((room: Room | undefined) => {
              if (room) {
                return (
                  <ChatBubble room={room} key={room.id} disabled={false} />
                );
              } else {
                return null;
              }
            })}
          </HStack>
        </ScrollView>
      );
    } else {
      return (
        <ErrorAndRefetch
          onRefetch={refetch}
          error={
            isError
              ? error?.message ?? 'Unknown Error Occurred'
              : !isClient
              ? `${user.username} isn't part of any public chats yet.`
              : "You aren't part of any public chats yet."
          }
        />
      );
    }
  };

  return (
    <VStack>
      <View px={8} mb={5}>
        <HStack alignItems={'center'}>
          <HStack alignItems={'baseline'} space={3}>
            <Text variant={'headerMini'}>Chats</Text>
            {!isLoading && chats && chats.length > 0 ? (
              <Text
                fontWeight={700}
                fontSize={16}
                color={theme.colors.surface[400]}
              >
                {chats.length}
              </Text>
            ) : null}
          </HStack>
          <Spacer />
        </HStack>
      </View>
      {isLoading ? (
        <ScrollView
          showsHorizontalScrollIndicator={true}
          horizontal={true}
          _contentContainerStyle={{
            px: 8,
            py: 5,
          }}
        >
          <Skeleton
            key={0}
            width="70px"
            height="70px"
            rounded="full"
            startColor={theme.colors.surface[200]}
            endColor={theme.colors.surface[400]}
          />
        </ScrollView>
      ) : (
        renderChats()
      )}
    </VStack>
  );
}

export default React.memo(ChatListCollectionView);
